<script setup lang="ts">
definePageMeta({
  middleware: ['sync-locale'],
})

const route = useRoute()
const formula = route.params.formula as string
const category = route.params.category as string
const lang = route.params.lang as string
const menu = route.params.menu as string

const { styles } = useVariants('page')
const theme = useTheme()

const { data, error, status } = useFetch<MenuDetailResponse>(
  `/api/menu/${formula}/${category}/${lang}/${menu}`,
)

watch(error, (err) => {
  if (err) showError({ statusCode: err.statusCode ?? 500, fatal: true })
}, { immediate: true })

const allIcons = computed<IconDetail[]>(() => data.value?.hotel.menu.icons ?? [])
const filter = provideAllergenFilter(allIcons)

const hasAnyVisible = computed(() => {
  const blocks = data.value?.hotel.menu.blocks
  if (!blocks) return true
  return blocks.some((block) => {
    if (block.type !== 'category') return false
    return block.items.some(filter.isItemVisible)
      || block.subcategories.some(sub => sub.items.some(filter.isItemVisible))
  })
})

watch(() => data.value?.hotel?.menu?.theme?.url, (url) => {
  if (url) theme.value = toThemeName(url)
}, { immediate: true, flush: 'sync' })

useHead(() => ({
  bodyAttrs: {
    'data-theme': theme.value,
  },
}))
</script>

<template>
  <div>
    <!--
      The whole menu body depends on the client-only allergen filter (initialized
      from sessionStorage), so it is inherently client-rendered. Wrapping it in
      <ClientOnly> makes that explicit and avoids hydration mismatches.
    -->
    <ClientOnly>
      <FilterBar v-if="data?.hotel.menu.icons?.length && filter.initialized.value" :icons="data.hotel.menu.icons" />
      <section :class="styles.menuSection()">
        <!-- Loading state -->
        <template v-if="status === 'pending' || (allIcons.length > 0 && !filter.initialized.value)">
          <Spinner />
        </template>
        <!-- Success state -->
        <template v-else-if="data?.hotel">
          <!-- Logo -->
          <div class="mb-8 flex justify-center">
            <ThemeLogo />
          </div>
          <!-- Notice -->
          <div :class="styles.menuNotice()">
            <strong>{{ $t('iconInfoHint') }}</strong>
          </div>
          <!-- Blocks -->
          <div :id="data?.hotel.menu.url" :class="styles.menuGrid()">
            <template v-for="(block, i) in data.hotel.menu.blocks" :key="i">
              <MenuCategory v-if="block.type === 'category'" :category="block" />
              <MenuTextBlock v-else-if="block.type === 'textblock'" :block="block" />
              <MenuTitleBlock v-else-if="block.type === 'title'" :text="block.text" />
            </template>
          </div>
          <!-- No results -->
          <p v-if="!hasAnyVisible" :class="styles.filterNoResults()">
            {{ $t('filterNoResults') }}
          </p>
        </template>
      </section>
      <AllergiesList v-if="data?.hotel.menu.icons?.length && filter.initialized.value" :icons="data.hotel.menu.icons" />

      <!-- Server / pre-hydration fallback (matches the previous server render) -->
      <template #fallback>
        <section :class="styles.menuSection()">
          <Spinner />
        </section>
      </template>
    </ClientOnly>
  </div>
</template>
